import { getDatatypes, IDatatype } from "@/api/datatypes";
import { SearchField } from "@/components/admin";
import { Table } from "@/components/shared/table/Table";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { Chip } from "@mui/material";
import { GridColDef, GridSortModel } from "@mui/x-data-grid-pro";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { DatatypeSchemaButton } from "./DatatypeSchemaButton";

const columns: GridColDef<IDatatype>[] = [
    {
        field: "id",
        headerName: "ID",
        display: "flex",
        width: 80,
    },
    {
        field: "name",
        headerName: "Name",
        display: "flex",
        flex: 1,
        minWidth: 150,
    },
    {
        field: "description",
        headerName: "Description",
        display: "flex",
        flex: 2,
        minWidth: 200,
    },
    {
        field: "is_system",
        headerName: "Type",
        display: "flex",
        width: 120,
        renderCell: ({ row }) => (
            <Chip
                label={row.is_system ? "System" : "Custom"}
                size="small"
                color={row.is_system ? "primary" : "default"}
                variant={row.is_system ? "filled" : "outlined"}
            />
        ),
    },
    {
        field: "complexity",
        headerName: "Complexity",
        display: "flex",
        filterable: false,
        sortable: false,
        width: 120,
        valueGetter: (_, row) =>
            row.datatype_schema && row.datatype_schema.length > 0
                ? "Complex"
                : "Base",
        renderCell: ({ row }) => {
            const isComplex =
                row.datatype_schema && row.datatype_schema.length > 0;
            return (
                <Chip
                    label={isComplex ? "Complex" : "Base"}
                    size="small"
                    color={isComplex ? "secondary" : "default"}
                    variant="outlined"
                />
            );
        },
    },
    {
        field: "properties",
        headerName: "Properties",
        display: "flex",
        width: 100,
        filterable: false,
        sortable: false,
        valueGetter: (_, row) => row.datatype_schema?.length || 0,
        renderCell: ({ row }) => {
            const count = row.datatype_schema?.length || 0;
            return count > 0 ? count : "-";
        },
    },
    {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        width: 100,
        getActions: ({ row }) => {
            const actions = [];
            if (row.datatype_schema && row.datatype_schema.length > 0) {
                actions.push(<DatatypeSchemaButton datatype={row} />);
            }
            return actions;
        },
    },
];

export const DatatypesTable = () => {
    const {
        search,
        debouncedSearch,
        setSearch,
        clear: handleSearchClear,
    } = useDebouncedSearch();

    const [rows, setRows] = useState<IDatatype[]>([]);

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });
    const [sortModel, setSortModel] = useState<GridSortModel>([
        { field: "name", sort: "asc" },
    ]);

    useEffect(() => {
        setPaginationModel((prev) =>
            prev.page === 0 ? prev : { ...prev, page: 0 }
        );
    }, [debouncedSearch, sortModel]);

    const sortFilter =
        sortModel.length > 0
            ? { sort_by: sortModel[0].field, order: sortModel[0].sort ?? "asc" }
            : {};

    const { data, isFetching } = useQuery({
        queryKey: [
            "admin-datatypes",
            paginationModel.page,
            paginationModel.pageSize,
            debouncedSearch,
            sortModel,
        ],
        queryFn: () =>
            getDatatypes(
                paginationModel.page + 1,
                paginationModel.pageSize,
                sortFilter,
                debouncedSearch || undefined
            ),
    });

    useEffect(() => {
        if (data) {
            setRows(data.items);
        }
    }, [data]);

    const rowCountRef = useRef(data?.total || 0);

    const rowCount = useMemo(() => {
        if (data?.total !== undefined) {
            rowCountRef.current = data?.total;
        }
        return rowCountRef.current;
    }, [data?.total]);

    return (
        <>
            <div className="mb-4">
                <SearchField
                    value={search}
                    onChange={setSearch}
                    onClear={handleSearchClear}
                    placeholder="Search datatypes..."
                />
            </div>
            <Table
                loading={isFetching}
                columns={columns}
                rows={rows}
                disableColumnFilter
                pageSizeOptions={[10, 25, 50]}
                paginationModel={paginationModel}
                rowCount={rowCount}
                onPaginationModelChange={setPaginationModel}
                paginationMode="server"
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={setSortModel}
            />
        </>
    );
};
